import cloverLogo from './logos/clover.svg';
import squareLogo from './logos/square.svg';
import toastLogo from './logos/toast.svg';
import lightspeedLogo from './logos/lightspeed.svg';

// Artwork only — deliberately not display names.
//
// `provider` is already the human-readable name on the server ("Lightspeed
// Retail", "POS 360"), and two providers can share a logo while being entirely
// different integrations: "Lightspeed" and "Lightspeed Retail" are separate
// products with separate credentials. Renaming either one here would make the
// tiles lie about which is which.
const LOGOS = {
  clover: cloverLogo,
  cloverv2: cloverLogo,
  square: squareLogo,
  toast: toastLogo,
  lightspeed: lightspeedLogo,
  'lightspeed retail': lightspeedLogo,
};

/** Title-case a snake_case or kebab-case *field* key for a form label. */
export const humanizeKey = (key) =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * How to present a provider.
 *
 * The name is the provider string exactly as given — never reformatted — so the
 * tile matches what the operator will see everywhere else. `descriptor.name` is
 * only consulted for the hardcoded v2 entries, which have no server record.
 */
export const providerPresentation = (descriptorOrKey) => {
  const descriptor =
    typeof descriptorOrKey === 'string' ? { provider: descriptorOrKey } : descriptorOrKey || {};
  const key = descriptor.provider || '';

  return {
    name: descriptor.name || key,
    logo: LOGOS[key.toLowerCase()] || null,
    blurb: descriptor.blurb || '',
  };
};

/** Two-letter mark for providers we have no logo for. */
export const providerInitials = (descriptorOrKey) => {
  const name = providerPresentation(descriptorOrKey).name || '?';
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// Keys on the descriptor that are metadata or server-managed, not things an
// operator fills in. Mirrors stockroom's list, plus `oauth_link`, which is a
// server-issued URL rather than an input.
export const NON_FIELD_KEYS = new Set([
  '_id',
  '__v',
  'account',
  'account_id',
  'provider',
  'master',
  'is_active',
  'required_fields',
  'provide_oauth_link',
  'oauth_link',
  'instructions',
  'access_token',
  'refresh_token',
  'last_updated',
  // Local-only keys on the hardcoded v2 entries.
  'name',
  'blurb',
]);

/**
 * Derive the editable form fields from a provider descriptor.
 *
 * The descriptor *is* the template: each remaining key becomes an input whose
 * type is decided by the type of its value.
 *
 * @returns {Array<{key: string, label: string, type: 'text'|'number'|'boolean', required: boolean}>}
 */
export const fieldsFromDescriptor = (descriptor) => {
  if (!descriptor) return [];
  const required = new Set(descriptor.required_fields || []);

  return Object.keys(descriptor)
    .filter((key) => !NON_FIELD_KEYS.has(key))
    .map((key) => {
      const value = descriptor[key];
      let type = 'text';
      if (typeof value === 'boolean') type = 'boolean';
      else if (typeof value === 'number') type = 'number';
      else if (value !== null && typeof value === 'object') return null; // nested objects aren't editable here
      return { key, label: humanizeKey(key), type, required: required.has(key) };
    })
    .filter(Boolean);
};

/** Seed form values from the descriptor, overlaid with anything already saved. */
export const initialValuesFor = (descriptor, saved = {}, { storeId } = {}) => {
  const values = {};
  fieldsFromDescriptor(descriptor).forEach(({ key, type }) => {
    const savedValue = saved?.[key];
    const seed = savedValue !== undefined && savedValue !== null ? savedValue : descriptor[key];

    if (type === 'boolean') {
      values[key] = !!seed;
    } else if (type === 'number') {
      values[key] = typeof seed === 'number' ? seed : '';
    } else {
      values[key] = typeof seed === 'string' ? withStoreId(seed, storeId) : '';
    }
  });
  return values;
};

/** Required fields the operator has left empty, as display labels. */
export const missingRequired = (descriptor, values) =>
  fieldsFromDescriptor(descriptor)
    .filter(({ key, required, type }) => {
      if (!required) return false;
      const value = values[key];
      if (type === 'boolean') return false; // false is a legitimate answer
      if (type === 'number') return value === '' || value === null || value === undefined;
      return !String(value ?? '').trim();
    })
    .map(({ label }) => label);

/** The catalogue ships templated values such as ".../{store_id}/callback". */
export const withStoreId = (value, storeId) =>
  typeof value === 'string' ? value.replace(/\{store_id\}/g, storeId || '') : value;

/**
 * Whether this provider authorizes rather than taking typed-in details.
 *
 * Either signal counts: the master record's `provide_oauth_link` flag, or the
 * presence of an `oauth_link` to send the operator to.
 */
export const usesOAuth = (descriptor) =>
  !!descriptor?.provide_oauth_link || !!descriptor?.oauth_link;

/** The store-specific authorization URL, or null. */
export const oauthLinkFor = (descriptor, storeId) =>
  descriptor?.oauth_link ? withStoreId(descriptor.oauth_link, storeId) : null;
